import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../utils/api';
import FormModal from '../components/FormModal';
import ConfirmDialog from '../components/ConfirmDialog';

const VILLAGES = ['Beckley', 'Elsfield', 'Forest Hill', 'Horton-cum-Studley', 'Stanton St John'];

const EMPTY_FORM = {
  userName: '', email: '', requestBody: '', amount: '', status: 'pending', tags: [],
  dateOfBirth: '', village: '', schoolInfo: '', benefitStatement: '',
  amountGranted: '', decisionMeetingDate: '',
};

export default function Requests() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search state
  const [searchTag, setSearchTag] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchVillage, setSearchVillage] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (searchTag) params.set('tag', searchTag);
      if (searchEmail) params.set('email', searchEmail);
      if (searchName) params.set('userName', searchName);
      if (searchVillage) params.set('village', searchVillage);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const qs = params.toString();
      const data = await apiFetch(`/requests${qs ? `?${qs}` : ''}`, token);
      setRequests(data.items);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, searchTag, searchEmail, searchName, searchVillage, dateFrom, dateTo]);

  const fetchTags = useCallback(async () => {
    try {
      const data = await apiFetch('/tags', token);
      setTags(data.items);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  function toggleTag(name) {
    setForm(f => ({
      ...f,
      tags: f.tags.includes(name) ? f.tags.filter(t => t !== name) : [...f.tags, name],
    }));
  }

  function openCreate() {
    setForm(EMPTY_FORM);
    setShowCreate(true);
  }

  async function openEdit(item) {
    setError('');
    try {
      const detail = await apiFetch(`/requests/${item.id}`, token);
      setForm({
        userName: detail.userName,
        email: detail.email,
        requestBody: detail.requestBody,
        amount: detail.amount ?? '',
        status: detail.status,
        tags: detail.tags || [],
        dateOfBirth: detail.dateOfBirth ?? '',
        village: detail.village ?? '',
        schoolInfo: detail.schoolInfo ?? '',
        benefitStatement: detail.benefitStatement ?? '',
        amountGranted: detail.amountGranted ?? '',
        decisionMeetingDate: detail.decisionMeetingDate ?? '',
      });
      setEditItem(detail);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleCreate() {
    setSaving(true);
    try {
      await apiFetch('/requests', token, { method: 'POST', body: form });
      setShowCreate(false);
      fetchRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    setSaving(true);
    try {
      await apiFetch(`/requests/${editItem.id}`, token, { method: 'PUT', body: form });
      setEditItem(null);
      fetchRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await apiFetch(`/requests/${deleteItem.id}`, token, { method: 'DELETE' });
      setDeleteItem(null);
      fetchRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function statusBadge(status) {
    const cls = status === 'approved' ? 'bg-success' : status === 'denied' ? 'bg-danger' : 'bg-warning text-dark';
    return <span className={`badge ${cls}`}>{status}</span>;
  }

  const formFields = (
    <>
      <div className="mb-2">
        <label className="form-label small mb-1">Name</label>
        <input className="form-control form-control-sm" value={form.userName}
          onChange={e => setForm({ ...form, userName: e.target.value })} required />
      </div>
      <div className="mb-2">
        <label className="form-label small mb-1">Email</label>
        <input type="email" className="form-control form-control-sm" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })} required />
      </div>
      <div className="mb-2">
        <label className="form-label small mb-1">Request</label>
        <textarea className="form-control form-control-sm" rows={3} value={form.requestBody}
          onChange={e => setForm({ ...form, requestBody: e.target.value })} required />
      </div>
      <div className="mb-2">
        <label className="form-label small mb-1">How this will benefit them</label>
        <textarea className="form-control form-control-sm" rows={2} value={form.benefitStatement}
          onChange={e => setForm({ ...form, benefitStatement: e.target.value })} />
      </div>
      <div className="row">
        <div className="col-6 mb-2">
          <label className="form-label small mb-1">Date of birth (YYYY-MM)</label>
          <input className="form-control form-control-sm" placeholder="e.g. 2006-02" value={form.dateOfBirth}
            onChange={e => setForm({ ...form, dateOfBirth: e.target.value })} />
        </div>
        <div className="col-6 mb-2">
          <label className="form-label small mb-1">Village</label>
          <select className="form-select form-select-sm" value={form.village}
            onChange={e => setForm({ ...form, village: e.target.value })}>
            <option value="">Select...</option>
            {VILLAGES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="mb-2">
        <label className="form-label small mb-1">School / education history</label>
        <input className="form-control form-control-sm" value={form.schoolInfo}
          onChange={e => setForm({ ...form, schoolInfo: e.target.value })} />
      </div>
      <div className="row">
        <div className="col-6 mb-2">
          <label className="form-label small mb-1">Amount requested (&pound;)</label>
          <input type="number" step="0.01" min="0" className="form-control form-control-sm" value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })} />
        </div>
        <div className="col-6 mb-2">
          <label className="form-label small mb-1">Status</label>
          <select className="form-select form-select-sm" value={form.status}
            onChange={e => setForm({ ...form, status: e.target.value })}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
        </div>
      </div>
      <div className="row">
        <div className="col-6 mb-2">
          <label className="form-label small mb-1">Amount granted (&pound;)</label>
          <input type="number" step="0.01" min="0" className="form-control form-control-sm" value={form.amountGranted}
            onChange={e => setForm({ ...form, amountGranted: e.target.value })} />
        </div>
        <div className="col-6 mb-2">
          <label className="form-label small mb-1">Decision meeting date</label>
          <input type="date" className="form-control form-control-sm" value={form.decisionMeetingDate}
            onChange={e => setForm({ ...form, decisionMeetingDate: e.target.value })} />
        </div>
      </div>
      <div className="mb-1">
        <label className="form-label small mb-1">Tags</label>
        <div className="d-flex flex-wrap gap-1">
          {tags.length === 0 && (
            <small className="text-muted fst-italic">No tags defined yet — create some on the Tags tab.</small>
          )}
          {tags.map(t => {
            const selected = form.tags.includes(t.name);
            return (
              <button type="button" key={t.name}
                className={`badge border ${selected ? 'bg-info text-dark border-info' : 'bg-white text-muted'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleTag(t.name)}>
                {selected ? '✓ ' : ''}{t.name}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <div>
      {error && <div className="alert alert-danger alert-dismissible py-1 mb-2">
        <small>{error}</small>
        <button type="button" className="btn-close btn-close-sm" style={{ padding: '0.5rem' }} onClick={() => setError('')} />
      </div>}

      {/* Search bar */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label small mb-0">Tag</label>
              <select className="form-select form-select-sm" value={searchTag} onChange={e => setSearchTag(e.target.value)}>
                <option value="">All</option>
                {tags.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-0">Name</label>
              <input className="form-control form-control-sm" value={searchName}
                onChange={e => setSearchName(e.target.value)} placeholder="Search..." />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-0">Email</label>
              <input className="form-control form-control-sm" value={searchEmail}
                onChange={e => setSearchEmail(e.target.value)} placeholder="Search..." />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-0">Village</label>
              <select className="form-select form-select-sm" value={searchVillage} onChange={e => setSearchVillage(e.target.value)}>
                <option value="">All</option>
                {VILLAGES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-0">From</label>
              <input type="date" className="form-control form-control-sm" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-0">To</label>
              <input type="date" className="form-control form-control-sm" value={dateTo}
                onChange={e => setDateTo(e.target.value)} />
            </div>
            <div className="col-md-2 d-flex gap-1">
              <button className="btn btn-sm btn-outline-primary flex-grow-1" onClick={fetchRequests}>Search</button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                setSearchTag(''); setSearchEmail(''); setSearchName(''); setDateFrom(''); setDateTo('');
              }}>Clear</button>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">{requests.length} request{requests.length !== 1 ? 's' : ''}</small>
        <button className="btn btn-sm btn-primary" onClick={openCreate}>New Request</button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
      ) : requests.length === 0 ? (
        <p className="text-muted text-center py-4">No requests found.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Email</th>
                <th>Village</th>
                <th>Requested</th>
                <th>Granted</th>
                <th>Status</th>
                <th style={{ width: '1%' }}></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td><small>{new Date(r.createdAt).toLocaleDateString()}</small></td>
                  <td><small>{r.userName}</small></td>
                  <td><small>{r.email}</small></td>
                  <td><small>{r.village || '—'}</small></td>
                  <td><small>{r.amount != null ? `£${Number(r.amount).toFixed(2)}` : '—'}</small></td>
                  <td><small>{r.amountGranted != null ? `£${Number(r.amountGranted).toFixed(2)}` : '—'}</small></td>
                  <td>{statusBadge(r.status)}</td>
                  <td className="text-nowrap text-end">
                    <button className="btn btn-link btn-sm p-0 me-2" onClick={() => openEdit(r)} title="Edit request">Edit</button>
                    <button className="btn btn-link btn-sm p-0 text-danger text-decoration-none" onClick={() => setDeleteItem(r)} title="Delete request">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <FormModal title="New Request" onClose={() => setShowCreate(false)} onSubmit={handleCreate} loading={saving}>
          {formFields}
        </FormModal>
      )}

      {/* Edit modal */}
      {editItem && (
        <FormModal title="Edit Request" onClose={() => setEditItem(null)} onSubmit={handleUpdate} loading={saving}>
          {formFields}
        </FormModal>
      )}

      {/* Delete confirm */}
      {deleteItem && (
        <ConfirmDialog
          title="Delete Request"
          message={`Delete request from ${deleteItem.userName}? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteItem(null)}
          loading={saving}
        />
      )}
    </div>
  );
}
